import type { Role } from '@sunstone/api-client/gen/tenants/v1/tenants_pb';
import { Badge } from '@sunstone/ui';
import { permissionActionLabel, RESOURCE_KIND_LABELS, resourceKindOf } from '@/lib/permissions';

function rolePermissionGroups(role: Role) {
  const byKind = new Map<string, string[]>();
  for (const permission of role.permissions) {
    const kind = resourceKindOf(permission);
    const list = byKind.get(kind) ?? [];
    list.push(permissionActionLabel(permission));
    byKind.set(kind, list);
  }
  return Array.from(byKind.entries()).map(([kind, actions]) => ({
    label: RESOURCE_KIND_LABELS[kind] ?? kind,
    actions,
  }));
}

interface RolePermissionSummaryProps {
  role: Role;
  /** Whether this role scopes to one customer or the whole platform — changes the wording only. */
  scopeLabel: string;
  /** 'compact' (Who-step style: "<role> can …") or 'detailed' (Confirm-step style, with a grantsAll badge). */
  variant: 'compact' | 'detailed';
}

/**
 * The grouped-by-resource-kind permission summary, shared by the invite
 * wizard's Who and Confirm steps and the member-detail edit modal's
 * Confirm step — previously duplicated between the two invite steps.
 */
export function RolePermissionSummary({ role, scopeLabel, variant }: RolePermissionSummaryProps) {
  const groups = rolePermissionGroups(role);

  if (variant === 'compact') {
    return (
      <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground">{role.name} can</p>
        {role.grantsAll ? (
          <p className="text-sm">
            Everything — full access across {scopeLabel}, including any permission added later.
          </p>
        ) : (
          <div className="space-y-1">
            {groups.map((group) => (
              <p key={group.label} className="text-sm">
                <span className="font-medium">{group.label}</span> — {group.actions.join(', ')}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        <p className="text-xs font-medium text-muted-foreground">Role</p>
        <Badge variant="outline" className="text-[10px]">
          {role.grantsAll ? 'Always current' : 'Fixed list'}
        </Badge>
      </div>
      <p className="text-sm font-medium">{role.name}</p>
      <div className="mt-1 space-y-0.5">
        {role.grantsAll ? (
          <p className="text-sm text-muted-foreground">
            Always current: automatically includes every permission this role has, now and any added
            to it later — full access across {scopeLabel}.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Fixed list: only the permissions below, even if the role gains more later.
            </p>
            {groups.map((group) => (
              <p key={group.label} className="text-sm text-muted-foreground">
                {group.label} — {group.actions.join(', ')}
              </p>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
